import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import pic from "../assets/images/cookie.jpg";

const Cookie = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const cardVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.7,
        ease: "easeOut"
      }
    }
  };

  const navigate = useNavigate();

  return (
    <motion.section
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="min-h-screen bg-white"
    >
      {/* Hero Section - Fixed */}
      <motion.div
        variants={itemVariants}
        className="relative h-96 md:h-[500px] w-full overflow-hidden"
      >
        {/* Background Image */}
        <img
          src={pic}
          alt="Cookie Policy Background"
          className="w-full h-full object-cover"
        />

        {/* Overlay */}
        <div className="absolute inset-0 bg-black/60 z-10" />

        {/* Content */}
        <div className="absolute inset-0 z-20 flex flex-col justify-center items-center text-center px-4">
          <motion.h1
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4"
          >
            Cookie Policy
          </motion.h1>
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: "100px" }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="h-1 bg-yellow-500 mb-4"
          />
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="text-lg md:text-xl text-white max-w-2xl mx-auto"
          >
            Learn how Geocode uses cookies to enhance your experience on our website.
          </motion.p>
        </div>
      </motion.div>

      {/* Main Content */}
      <div className="py-16 px-4 sm:px-6 lg:px-8">
        {/* Content Grid */}
        <motion.div
          variants={containerVariants}
          className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8"
        >
          {/* What Are Cookies */}
          <motion.div
            variants={cardVariants}
            whileHover={{
              y: -5,
              transition: { duration: 0.3 }
            }}
            className="bg-white border-2 border-gray-200 rounded-xl p-8 shadow-lg hover:shadow-xl transition-all h-full"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-yellow-500 rounded-full flex items-center justify-center mr-4">
                <span className="text-2xl">🍪</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">What Are Cookies?</h2>
            </div>
            <p className="text-gray-700 leading-relaxed">
              Cookies are small text files stored on your device by your web browser. They help improve website functionality, track usage, and enhance user experience.
            </p>
          </motion.div>

          {/* Types of Cookies */}
          <motion.div
            variants={cardVariants}
            whileHover={{
              y: -5,
              transition: { duration: 0.3 }
            }}
            className="bg-white border-2 border-gray-200 rounded-xl p-8 shadow-lg hover:shadow-xl transition-all h-full"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center mr-4">
                <span className="text-2xl text-white">⚙️</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Types of Cookies We Use</h2>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <span className="text-yellow-500 mr-3 mt-1">•</span>
                <span><strong className="text-gray-900">Essential Cookies:</strong> Required for website functionality.</span>
              </li>
              <li className="flex items-start">
                <span className="text-yellow-500 mr-3 mt-1">•</span>
                <span><strong className="text-gray-900">Performance Cookies:</strong> Track usage to improve performance.</span>
              </li>
              <li className="flex items-start">
                <span className="text-yellow-500 mr-3 mt-1">•</span>
                <span><strong className="text-gray-900">Functional Cookies:</strong> Remember user preferences for convenience.</span>
              </li>
              <li className="flex items-start">
                <span className="text-yellow-500 mr-3 mt-1">•</span>
                <span><strong className="text-gray-900">Targeting/Advertising Cookies:</strong> Help deliver relevant content or ads.</span>
              </li>
            </ul>
          </motion.div>

          {/* How We Use Cookies */}
          <motion.div
            variants={cardVariants}
            whileHover={{
              y: -5,
              transition: { duration: 0.3 }
            }}
            className="bg-white border-2 border-gray-200 rounded-xl p-8 shadow-lg hover:shadow-xl transition-all h-full"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-yellow-500 rounded-full flex items-center justify-center mr-4">
                <span className="text-2xl">🔍</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">How We Use Cookies</h2>
            </div>
            <p className="text-gray-700 leading-relaxed">
              Cookies help us analyze traffic, personalize content, remember your preferences, and provide a smooth browsing experience.
            </p>
          </motion.div>

          {/* Managing Cookies */}
          <motion.div
            variants={cardVariants}
            whileHover={{
              y: -5,
              transition: { duration: 0.3 }
            }}
            className="bg-white border-2 border-gray-200 rounded-xl p-8 shadow-lg hover:shadow-xl transition-all h-full"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center mr-4">
                <span className="text-2xl text-white">⚙️</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Managing Cookies</h2>
            </div>
            <p className="text-gray-700 leading-relaxed">
              You can manage cookies through your browser settings. You may block or delete cookies, but some features of the website may not function properly if cookies are disabled.
            </p>
          </motion.div>

          {/* Third-Party Cookies */}
          <motion.div
            variants={cardVariants}
            whileHover={{
              y: -5,
              transition: { duration: 0.3 }
            }}
            className="bg-white border-2 border-gray-200 rounded-xl p-8 shadow-lg hover:shadow-xl transition-all h-full"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-yellow-500 rounded-full flex items-center justify-center mr-4">
                <span className="text-2xl">🌐</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Third-Party Cookies</h2>
            </div>
            <p className="text-gray-700 leading-relaxed">
              We may allow third-party services like Google Analytics to place cookies on your device. These are used to collect anonymous data about website usage.
            </p>
          </motion.div>

          {/* Consent */}
          <motion.div
            variants={cardVariants}
            whileHover={{
              y: -5,
              transition: { duration: 0.3 }
            }}
            className="bg-white border-2 border-gray-200 rounded-xl p-8 shadow-lg hover:shadow-xl transition-all h-full"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center mr-4">
                <span className="text-2xl text-white">✅</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Your Consent</h2>
            </div>
            <p className="text-gray-700 leading-relaxed">
              By using our website, you consent to the use of cookies as described in this policy. You can withdraw consent at any time by adjusting your browser settings.
            </p>
          </motion.div>
        </motion.div>

        {/* Footer Section */}
        <motion.div
          variants={itemVariants}
          className=" text-center mb-6 mx-4"
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="bg-black text-white p-8 rounded-2xl shadow-2xl mt-10"
          >
            <h3 className="text-2xl md:text-3xl font-bold mb-4 text-yellow-400">
              Need Further Assistance?
            </h3>
            <p className="text-gray-300 text-lg mb-6">
              Our team is here to help you understand our cookie policy. Don't hesitate to reach out if you have any questions.
            </p>



            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center w-full px-4">
              <motion.button
                whileHover={{
                  scale: 1.05,
                  boxShadow: "0 15px 30px -5px rgba(255,215,0,0.3)"
                }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate("/")}
                className="w-full sm:w-auto bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-gray-900 px-8 py-3 rounded-lg text-lg font-semibold shadow-lg transition-all duration-300"
              >
                Back to Home
              </motion.button>

              <motion.button
                whileHover={{
                  scale: 1.05,
                  boxShadow: "0 15px 30px -5px rgba(255,215,0,0.3)"
                }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate("/contact")}
                className="w-full sm:w-auto bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-gray-900 px-8 py-3 rounded-lg text-lg font-semibold shadow-lg transition-all duration-300"
              >
                Contact Us
              </motion.button>
            </div>

          </motion.div>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default Cookie;